import { useState } from "react";
import AlertThresholds from "./AlertThresholds.jsx";
import { InlineMessage } from "../ui/Feedback.jsx";
import DocsLink from "../ui/DocsLink.jsx";

/**
 * SettingsPage
 * Props:
 *   apiKey — string, safe to display
 *   onRevealSecret() — triggers a 2FA flow elsewhere; this component never
 *                        holds the raw secret itself
 *   webhook — { callbackUrl, mode: 'notify_only'|'content' }
 *   onTestWebhook()
 *   onUpdateWebhook({ callbackUrl, mode })
 *   downtimeWindows — [{ id, label, status }]
 *   onAddDowntime()
 *   onRemoveDowntime(id)
 *   plan — { name, usedFeeds, totalFeeds, forecast }
 *   onChangePlan()
 */
export default function SettingsPage({
  projectId,
  // The project's lifecycle status. `mode` below is how we deliver; this is whether
  // we deliver at all, and the two read as the same word to a customer unless the
  // screen says which one it means.
  projectStatus = "active",
  apiKey = "pk_live_9f2a...c31",
  onRevealSecret,
  webhook,
  onTestWebhook,
  onUpdateWebhook,
  downtimeWindows = [],
  onAddDowntime,
  onRemoveDowntime,
  plan,
  onChangePlan,
}) {
  const [callbackUrl, setCallbackUrl] = useState(webhook?.callbackUrl || "");
  const [mode, setMode] = useState(webhook?.mode || "notify_only");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <p style={{ fontWeight: 500, fontSize: "var(--text-md)", margin: "0 0 2px" }}>API credentials</p>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 12px" }}>
          Used to authenticate requests to the iCal subscription API. The header name, the
          batch endpoint and runnable <code>curl</code> examples are in the{" "}
          <DocsLink />.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="ical-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>Project API key</p>
              <p style={{ margin: 0, fontSize: "var(--text-base)", fontFamily: "var(--font-mono)" }}>{apiKey}</p>
            </div>
            <button type="button" className="ical-btn" style={{ padding: "4px 10px", fontSize: "var(--text-xs)" }} onClick={() => navigator.clipboard?.writeText(apiKey)}>
              Copy
            </button>
          </div>
          <div className="ical-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>Project secret</p>
              <p style={{ margin: 0, fontSize: "var(--text-base)", fontFamily: "var(--font-mono)" }}>••••••••••••••••</p>
            </div>
            <button type="button" className="ical-btn" style={{ padding: "4px 10px", fontSize: "var(--text-xs)" }} onClick={onRevealSecret}>
              Reveal (requires code)
            </button>
          </div>
        </div>
      </div>

      <div style={{ borderTop: "1px solid var(--border)", paddingTop: 20 }}>
        <p style={{ fontWeight: 500, fontSize: "var(--text-md)", margin: "0 0 2px" }}>Webhook</p>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 12px" }}>Where change notifications are delivered.</p>
        {projectStatus !== "active" ? (
          <InlineMessage tone={projectStatus === "suspended" ? "warning" : "danger"}>
            {projectStatus === "suspended"
              ? "Deliveries are switched off for this project. Nothing is being sent to your endpoint until it is switched back on. Change notifications raised while it is off are held and sent in order once it is active again."
              : "Deliveries are suspended: your endpoint has been failing for long enough that we stopped sending to it. Change notifications are held, not dropped, and we keep checking your endpoint — press Test below to resume now, or wait for our next check. Fixing the URL is what will make the test pass."}
          </InlineMessage>
        ) : null}
        <label style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Callback URL</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input
            className="ical-input"
            type="url"
            style={{ flex: 1 }}
            value={callbackUrl}
            onChange={(e) => {
              setCallbackUrl(e.target.value);
              onUpdateWebhook?.({ callbackUrl: e.target.value, mode });
            }}
          />
          <button type="button" className="ical-btn" onClick={onTestWebhook}>Test</button>
        </div>
        <label style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Delivery mode</label>
        <select
          className="ical-select"
          style={{ width: "100%" }}
          value={mode}
          onChange={(e) => {
            setMode(e.target.value);
            onUpdateWebhook?.({ callbackUrl, mode: e.target.value });
          }}
        >
          <option value="notify_only">Notify only (IDs + URL)</option>
          <option value="content">Send changed content</option>
        </select>
      </div>

      {/* Between the webhook and the downtime window: all three are about what we
          do with a failing or a changing calendar, and the downtime window is the
          one setting that changes how the others behave. */}
      {projectId ? <AlertThresholds projectId={projectId} /> : null}

      <div style={{ borderTop: "1px solid var(--border)", paddingTop: 20 }}>
        <p style={{ fontWeight: 500, fontSize: "var(--text-md)", margin: "0 0 2px" }}>Scheduled downtime</p>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 12px" }}>
          Notifications are held, not dropped, during these windows.
        </p>
        {downtimeWindows.map((w) => (
          <div key={w.id} className="ical-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div>
              <p style={{ margin: 0, fontSize: "var(--text-sm)" }}>{w.label}</p>
              <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>{w.status}</p>
            </div>
            <button type="button" className="ical-btn" style={{ padding: "4px 10px", fontSize: "var(--text-xs)" }} onClick={() => onRemoveDowntime?.(w.id)}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" className="ical-btn" onClick={onAddDowntime}>+ Add downtime window</button>
      </div>

      <div style={{ borderTop: "1px solid var(--border)", paddingTop: 20 }}>
        <p style={{ fontWeight: 500, fontSize: "var(--text-md)", margin: "0 0 2px" }}>Billing</p>
        <div className="ical-card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <p style={{ margin: 0, fontSize: "var(--text-base)", fontWeight: 500 }}>{plan?.name} plan</p>
            <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
              {plan?.usedFeeds?.toLocaleString()} / {plan?.totalFeeds?.toLocaleString()} feeds · forecast: {plan?.forecast?.toLocaleString()} by period end
            </p>
          </div>
          <button type="button" className="ical-btn" onClick={onChangePlan}>Change plan</button>
        </div>
      </div>
    </div>
  );
}
